/**
 * AdditionalRiskIdentification - Manual hazard identification forms
 * Extracted from StepSpaces to keep file sizes manageable
 */

import React from 'react';
import { Switch } from '../../../../../catalyst/checkbox';
import WizardSection from '../../WizardSection';

const AdditionalRiskIdentification = ({ draftConfig, updateConfig }) => {
  return (
    <div>
      <h4 className="text-sm font-medium text-gray-700 mb-2">Additional Risk Identification</h4>
      <p className="text-xs text-gray-500 mb-3">
        Identify any additional hazards not automatically detected from your vessel configuration:
      </p>
      
      <div className="space-y-4">
        {/* Additional Gas Appliances */}
        <div>
          <label className="flex items-center justify-between">
            <div className="flex-1">
              <span className="text-sm font-medium text-gray-900">Additional Gas Appliances</span>
              <p className="text-xs text-gray-500 mt-1">Gas consuming appliances beyond cooking (e.g., gas heaters, water heaters)</p>
            </div>
            <Switch
              checked={Array.isArray(draftConfig.additionalGasAppliances) ? draftConfig.additionalGasAppliances.length > 0 : false}
              onChange={(checked) => {
                if (!checked) {
                  updateConfig({ additionalGasAppliances: [] });
                } else if (!Array.isArray(draftConfig.additionalGasAppliances) || draftConfig.additionalGasAppliances.length === 0) {
                  updateConfig({ additionalGasAppliances: [{ id: Date.now().toString(), applianceType: '', location: '' }] });
                }
              }}
            />
          </label>
          
          {Array.isArray(draftConfig.additionalGasAppliances) && draftConfig.additionalGasAppliances.length > 0 && (
            <div className="space-y-3 mt-3 pt-3 border-t border-gray-200">
              {draftConfig.additionalGasAppliances.map((appliance, index) => (
                <div key={appliance.id} className="bg-gray-50 p-3 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <WizardSection.Field
                      label="Appliance Type"
                      help="Type of gas appliance"
                    >
                      <WizardSection.Select
                        value={appliance.applianceType || ''}
                        onChange={(e) => {
                          const updated = [...draftConfig.additionalGasAppliances];
                          updated[index] = { ...appliance, applianceType: e.target.value };
                          updateConfig({ additionalGasAppliances: updated });
                        }}
                      >
                        <option value="">Select type...</option>
                        <option value="water_heater">Water Heater</option>
                        <option value="space_heater">Space Heater</option>
                        <option value="clothes_dryer">Clothes Dryer</option>
                        <option value="other">Other</option>
                      </WizardSection.Select>
                    </WizardSection.Field>
                    
                    <WizardSection.Field
                      label="Location"
                      help="Where is this appliance located?"
                    >
                      <WizardSection.Input
                        type="text"
                        value={appliance.location || ''}
                        onChange={(e) => {
                          const updated = [...draftConfig.additionalGasAppliances];
                          updated[index] = { ...appliance, location: e.target.value };
                          updateConfig({ additionalGasAppliances: updated });
                        }}
                        placeholder="e.g., Boiler Room, Engine Room"
                      />
                    </WizardSection.Field>
                  </div>
                  
                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
                    ✓ Requires hydrocarbon gas detector at low level in {appliance.location || 'this location'} per WBC3 15.6.4.1
                  </div>
                  
                  <button
                    type="button"
                    onClick={() => {
                      const updated = draftConfig.additionalGasAppliances.filter((_, i) => i !== index);
                      updateConfig({ additionalGasAppliances: updated });
                    }}
                    className="mt-2 text-xs text-red-600 hover:text-red-700"
                  >
                    Remove
                  </button>
                </div>
              ))}
              
              <button
                type="button"
                onClick={() => {
                  const updated = [...draftConfig.additionalGasAppliances, { 
                    id: Date.now().toString(), 
                    applianceType: '', 
                    location: '' 
                  }];
                  updateConfig({ additionalGasAppliances: updated });
                }}
                className="text-sm text-cyan-600 hover:text-cyan-700 font-medium"
              >
                + Add Another Gas Appliance
              </button>
            </div>
          )}
        </div>

        {/* Additional Exhaust Risk Areas */}
        <div>
          <label className="flex items-center justify-between">
            <div className="flex-1">
              <span className="text-sm font-medium text-gray-900">Additional Exhaust Risk Areas</span>
              <p className="text-xs text-gray-500 mt-1">Additional spaces where exhaust gases may accumulate beyond engine spaces</p>
            </div>
            <Switch
              checked={Array.isArray(draftConfig.additionalExhaustRisks) ? draftConfig.additionalExhaustRisks.length > 0 : false}
              onChange={(checked) => {
                if (!checked) {
                  updateConfig({ additionalExhaustRisks: [] });
                } else if (!Array.isArray(draftConfig.additionalExhaustRisks) || draftConfig.additionalExhaustRisks.length === 0) {
                  updateConfig({ additionalExhaustRisks: [{ id: Date.now().toString(), source: '', affectedSpace: '' }] });
                }
              }}
            />
          </label>
          
          {Array.isArray(draftConfig.additionalExhaustRisks) && draftConfig.additionalExhaustRisks.length > 0 && (
            <div className="space-y-3 mt-3 pt-3 border-t border-gray-200">
              {draftConfig.additionalExhaustRisks.map((risk, index) => (
                <div key={risk.id} className="bg-gray-50 p-3 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <WizardSection.Field
                      label="Exhaust Source"
                      help="Source of potential exhaust leak"
                    >
                      <WizardSection.Input
                        type="text"
                        value={risk.source || ''}
                        onChange={(e) => {
                          const updated = [...draftConfig.additionalExhaustRisks];
                          updated[index] = { ...risk, source: e.target.value };
                          updateConfig({ additionalExhaustRisks: updated });
                        }}
                        placeholder="e.g., Generator, Heater"
                      />
                    </WizardSection.Field>
                    
                    <WizardSection.Field
                      label="Affected Space"
                      help="Where exhaust could accumulate"
                    >
                      <WizardSection.Input
                        type="text"
                        value={risk.affectedSpace || ''}
                        onChange={(e) => {
                          const updated = [...draftConfig.additionalExhaustRisks];
                          updated[index] = { ...risk, affectedSpace: e.target.value };
                          updateConfig({ additionalExhaustRisks: updated });
                        }}
                        placeholder="e.g., Crew Quarters, Storage"
                      />
                    </WizardSection.Field>
                  </div>
                  
                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
                    ✓ Requires CO detector in {risk.affectedSpace || 'affected space'} per WBC3 15.6.3.3
                  </div>
                  
                  <button
                    type="button"
                    onClick={() => {
                      const updated = draftConfig.additionalExhaustRisks.filter((_, i) => i !== index);
                      updateConfig({ additionalExhaustRisks: updated });
                    }}
                    className="mt-2 text-xs text-red-600 hover:text-red-700"
                  >
                    Remove
                  </button>
                </div>
              ))}
              
              <button
                type="button"
                onClick={() => {
                  const updated = [...draftConfig.additionalExhaustRisks, { 
                    id: Date.now().toString(), 
                    source: '', 
                    affectedSpace: '' 
                  }];
                  updateConfig({ additionalExhaustRisks: updated });
                }}
                className="text-sm text-cyan-600 hover:text-cyan-700 font-medium"
              >
                + Add Another Exhaust Risk Area
              </button>
            </div>
          )}
        </div>

        {/* Concentrated Electrical Equipment */}
        <div>
          <label className="flex items-center justify-between">
            <div className="flex-1">
              <span className="text-sm font-medium text-gray-900">Concentrated Electrical Equipment</span>
              <p className="text-xs text-gray-500 mt-1">Areas with significant electrical equipment concentration (requires fire detectors)</p>
            </div>
            <Switch
              checked={Array.isArray(draftConfig.electricalEquipmentAreas) ? draftConfig.electricalEquipmentAreas.length > 0 : false}
              onChange={(checked) => {
                if (!checked) {
                  updateConfig({ electricalEquipmentAreas: [] });
                } else if (!Array.isArray(draftConfig.electricalEquipmentAreas) || draftConfig.electricalEquipmentAreas.length === 0) {
                  updateConfig({ electricalEquipmentAreas: [{ id: Date.now().toString(), spaceName: '', equipmentType: '' }] });
                }
              }}
            />
          </label>
          
          {Array.isArray(draftConfig.electricalEquipmentAreas) && draftConfig.electricalEquipmentAreas.length > 0 && (
            <div className="space-y-3 mt-3 pt-3 border-t border-gray-200">
              {draftConfig.electricalEquipmentAreas.map((area, index) => (
                <div key={area.id} className="bg-gray-50 p-3 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <WizardSection.Field
                      label="Space Name"
                      help="Location of electrical equipment"
                    >
                      <WizardSection.Input
                        type="text"
                        value={area.spaceName || ''}
                        onChange={(e) => {
                          const updated = [...draftConfig.electricalEquipmentAreas];
                          updated[index] = { ...area, spaceName: e.target.value };
                          updateConfig({ electricalEquipmentAreas: updated });
                        }}
                        placeholder="e.g., Server Room, Electrical Room"
                      />
                    </WizardSection.Field>
                    
                    <WizardSection.Field
                      label="Equipment Type"
                      help="Type of electrical equipment"
                    >
                      <WizardSection.Select
                        value={area.equipmentType || ''}
                        onChange={(e) => {
                          const updated = [...draftConfig.electricalEquipmentAreas];
                          updated[index] = { ...area, equipmentType: e.target.value };
                          updateConfig({ electricalEquipmentAreas: updated });
                        }}
                      >
                        <option value="">Select type...</option>
                        <option value="server_room">Server Room</option>
                        <option value="electrical_panels">Electrical Panels</option>
                        <option value="battery_room">Battery Room (non-propulsion)</option>
                        <option value="control_room">Control Room</option>
                        <option value="other">Other</option>
                      </WizardSection.Select>
                    </WizardSection.Field>
                  </div>
                  
                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
                    ✓ Requires fire detector(s) in {area.spaceName || 'this space'} per WBC3 15.6.2.1
                  </div>
                  
                  <button
                    type="button"
                    onClick={() => {
                      const updated = draftConfig.electricalEquipmentAreas.filter((_, i) => i !== index);
                      updateConfig({ electricalEquipmentAreas: updated });
                    }}
                    className="mt-2 text-xs text-red-600 hover:text-red-700"
                  >
                    Remove
                  </button>
                </div>
              ))}
              
              <button
                type="button"
                onClick={() => {
                  const updated = [...draftConfig.electricalEquipmentAreas, { 
                    id: Date.now().toString(), 
                    spaceName: '', 
                    equipmentType: '' 
                  }];
                  updateConfig({ electricalEquipmentAreas: updated });
                }}
                className="text-sm text-cyan-600 hover:text-cyan-700 font-medium"
              >
                + Add Another Electrical Equipment Area
              </button>
            </div>
          )}
        </div>

        {/* Additional Fire Risk Areas */}
        <div>
          <label className="flex items-center justify-between">
            <div className="flex-1">
              <span className="text-sm font-medium text-gray-900">Additional Fire Risk Areas</span>
              <p className="text-xs text-gray-500 mt-1">Areas identified as increased fire risk through risk assessment (requires fire blankets)</p>
            </div>
            <Switch
              checked={Array.isArray(draftConfig.fireRiskAreas) ? draftConfig.fireRiskAreas.length > 0 : false}
              onChange={(checked) => {
                if (!checked) {
                  updateConfig({ fireRiskAreas: [] });
                } else if (!Array.isArray(draftConfig.fireRiskAreas) || draftConfig.fireRiskAreas.length === 0) {
                  updateConfig({ fireRiskAreas: [{ id: Date.now().toString(), spaceName: '', riskType: '' }] });
                }
              }}
            />
          </label>
          
          {Array.isArray(draftConfig.fireRiskAreas) && draftConfig.fireRiskAreas.length > 0 && (
            <div className="space-y-3 mt-3 pt-3 border-t border-gray-200">
              {draftConfig.fireRiskAreas.map((area, index) => (
                <div key={area.id} className="bg-gray-50 p-3 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <WizardSection.Field
                      label="Space Name"
                      help="Location of fire risk"
                    >
                      <WizardSection.Input
                        type="text"
                        value={area.spaceName || ''}
                        onChange={(e) => {
                          const updated = [...draftConfig.fireRiskAreas];
                          updated[index] = { ...area, spaceName: e.target.value };
                          updateConfig({ fireRiskAreas: updated });
                        }}
                        placeholder="e.g., Paint Locker, Workshop"
                      />
                    </WizardSection.Field>
                    
                    <WizardSection.Field
                      label="Risk Type"
                      help="Type of fire risk"
                    >
                      <WizardSection.Select
                        value={area.riskType || ''}
                        onChange={(e) => {
                          const updated = [...draftConfig.fireRiskAreas];
                          updated[index] = { ...area, riskType: e.target.value };
                          updateConfig({ fireRiskAreas: updated });
                        }}
                      >
                        <option value="">Select type...</option>
                        <option value="paint_storage">Paint Storage</option>
                        <option value="workshop">Workshop/Hot Work</option>
                        <option value="chemical_storage">Chemical Storage</option>
                        <option value="waste_storage">Waste Storage</option>
                        <option value="other">Other</option>
                      </WizardSection.Select>
                    </WizardSection.Field>
                  </div>
                  
                  <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
                    ✓ Requires fire blanket in {area.spaceName || 'this area'} per WBC3 16.5
                  </div>
                  
                  <button
                    type="button"
                    onClick={() => {
                      const updated = draftConfig.fireRiskAreas.filter((_, i) => i !== index);
                      updateConfig({ fireRiskAreas: updated });
                    }}
                    className="mt-2 text-xs text-red-600 hover:text-red-700"
                  >
                    Remove
                  </button>
                </div>
              ))}
              
              <button
                type="button"
                onClick={() => {
                  const updated = [...draftConfig.fireRiskAreas, { 
                    id: Date.now().toString(), 
                    spaceName: '', 
                    riskType: '' 
                  }];
                  updateConfig({ fireRiskAreas: updated });
                }}
                className="text-sm text-cyan-600 hover:text-cyan-700 font-medium"
              >
                + Add Another Fire Risk Area
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdditionalRiskIdentification;